import { get, post, patch } from '../request';
import type { PaginatedData } from '../typings';
import type {
  SyncOrdersRequest,
  SyncSingleOrderRequest,
  SyncResponse,
  SyncStatusInfo
} from './sync.d';
import type { AmazonOrder, OrderListParams } from './orders.d';

/**
 * Amazon订单同步API服务
 */

// ============= 订单同步操作 =============

/**
 * 批量同步Amazon订单
 */
export async function syncOrders(data: SyncOrdersRequest = {}) {
  return post<SyncResponse>('/api/tenant/amazon/sync/orders', {
    days: 7,          // 默认同步最近7天
    async: true,      // 默认异步处理
    force: false,     // 默认不强制同步
    ...data,
  });
}

/**
 * 同步单个Amazon订单
 */
export async function syncSingleOrder(data: SyncSingleOrderRequest) {
  return post<SyncResponse>('/api/tenant/amazon/sync/orders/single', data);
}

/**
 * 获取同步状态
 */
export async function getSyncStatus() {
  return get<SyncStatusInfo>('/api/tenant/amazon/sync/status');
}

/**
 * 取消同步任务
 */
export async function cancelSyncJob(jobId: string) {
  return post<{
    success: boolean;
    message: string;
  }>(`/api/tenant/amazon/sync/jobs/${jobId}/cancel`);
}

/**
 * 重试失败的同步任务
 */
export async function retrySyncJob(jobId: string) {
  return post<SyncResponse>(`/api/tenant/amazon/sync/jobs/${jobId}/retry`);
}

// ============= 订单查询和管理 =============

/**
 * 获取Amazon订单列表
 */
export async function getOrders(params?: OrderListParams) {
  const queryParams = {
    platform: 'amazon',  // 固定查询Amazon订单
    ...params,
  };

  return get<PaginatedData<AmazonOrder>>('/api/tenant/orders', queryParams);
}

/**
 * 获取Amazon订单详情
 */
export async function getOrderDetail(orderId: number) {
  return get<AmazonOrder>(`/api/tenant/orders/${orderId}`, {
    include_items: true,           // 包含订单项
    include_sync_history: true,   // 包含同步历史
  });
}

/**
 * 通过平台订单ID查询订单
 */
export async function getOrderByPlatformId(platformOrderId: string) {
  return get<AmazonOrder>('/api/tenant/orders/by-platform-id', {
    platform: 'amazon',
    platform_order_id: platformOrderId,
  });
}

/**
 * 更新订单状态
 */
export async function updateOrderStatus(orderId: number, status: string) {
  return patch<AmazonOrder>(`/api/tenant/orders/${orderId}/status`, { status });
}

/**
 * 批量更新订单状态
 */
export async function batchUpdateOrderStatus(orderIds: number[], status: string) {
  return patch<{
    success_count: number;
    failed_count: number;
    errors?: string[];
  }>('/api/tenant/orders/batch/status', {
    order_ids: orderIds,
    status,
  });
}

// ============= 同步历史和统计 =============

/**
 * 获取订单同步历史
 */
export async function getOrderSyncHistory(orderId: number) {
  return get<Array<{
    id: number;
    sync_type: 'manual' | 'auto' | 'retry';
    status: 'success' | 'failed' | 'partial';
    message?: string;
    changes_count: number;
    sync_at: string;
  }>>(`/api/tenant/orders/${orderId}/sync-history`);
}

/**
 * 获取同步统计信息
 */
export async function getSyncStatistics(params?: {
  start_date?: string;
  end_date?: string;
  credential_id?: number;
}) {
  return get<{
    total_synced: number;
    total_created: number;
    total_updated: number;
    total_failed: number;
    success_rate: number;
    avg_sync_time: number;
    daily_stats: Array<{
      date: string;
      synced: number;
      created: number;
      updated: number;
      failed: number;
    }>;
  }>('/api/tenant/amazon/sync/statistics', params);
}

// ============= 错误处理和重试 =============

/**
 * 获取同步错误列表
 */
export async function getSyncErrors(params?: {
  current?: number;
  pageSize?: number;
  error_type?: string;
  start_date?: string;
  end_date?: string;
}) {
  return get<Array<{
    id: number;
    order_id?: number;
    platform_order_id: string;
    error_type: string;
    error_message: string;
    retry_count: number;
    last_retry_at?: string;
    resolved: boolean;
    created_at: string;
  }>>('/api/tenant/amazon/sync/errors', params);
}

/**
 * 重试同步错误
 */
export async function retrySyncError(errorId: number) {
  return post<{
    success: boolean;
    message: string;
    job_id?: string;
  }>(`/api/tenant/amazon/sync/errors/${errorId}/retry`);
}

/**
 * 标记错误为已解决
 */
export async function resolveSyncError(errorId: number) {
  return post<{
    success: boolean;
    message: string;
  }>(`/api/tenant/amazon/sync/errors/${errorId}/resolve`);
}

/**
 * 批量重试同步错误
 */
export async function batchRetrySyncErrors(errorIds: number[]) {
  return post<{
    success_count: number;
    failed_count: number;
    job_ids: string[];
    errors?: string[];
  }>('/api/tenant/amazon/sync/errors/batch-retry', { error_ids: errorIds });
}

// ============= 实用工具函数 =============

/**
 * 格式化同步状态显示
 */
export function formatSyncStatus(status: string) {
  const statusConfig: Record<string, { color: string; text: string }> = {
    pending: { color: 'processing', text: '待同步' },
    processing: { color: 'processing', text: '同步中' },
    completed: { color: 'success', text: '已完成' },
    failed: { color: 'error', text: '失败' },
    cancelled: { color: 'default', text: '已取消' },
  };

  return statusConfig[status] || { color: 'default', text: '未知' };
}

/**
 * 格式化订单同步状态
 */
export function formatOrderSyncStatus(order: AmazonOrder) {
  const { sync_status, last_sync_at } = order;

  const statusConfig: Record<string, { color: string; text: string }> = {
    synced: { color: 'success', text: '已同步' },
    pending: { color: 'processing', text: '待同步' },
    failed: { color: 'error', text: '同步失败' },
  };

  const config = statusConfig[sync_status] || { color: 'default', text: '未知' };

  return {
    ...config,
    lastSyncTime: last_sync_at ? new Date(last_sync_at).toLocaleString() : '从未同步',
  };
}

/**
 * 计算同步进度百分比
 */
export function calculateSyncProgress(progress: string): number {
  const match = progress.match(/(\d+)%/);
  return match ? parseInt(match[1], 10) : 0;
}

/**
 * 估算剩余同步时间
 */
export function estimateRemainingTime(progress: string, startTime: string): string {
  const progressPercent = calculateSyncProgress(progress);
  if (progressPercent <= 0) return '计算中...';

  const elapsed = Date.now() - new Date(startTime).getTime();
  const estimated = (elapsed / progressPercent) * (100 - progressPercent);

  const minutes = Math.ceil(estimated / 60000);

  if (minutes < 1) return '即将完成';
  if (minutes === 1) return '约1分钟';
  return `约${minutes}分钟`;
}

/**
 * 检查订单是否需要手动干预
 */
export function needsManualIntervention(order: AmazonOrder): boolean {
  return order.sync_status === 'failed' && order.sync_error !== null;
}

/**
 * 获取同步错误建议
 */
export function getSyncErrorSuggestion(errorMessage?: string): string {
  if (!errorMessage) return '请检查网络连接和凭据配置';

  const message = errorMessage.toLowerCase();

  if (message.includes('rate limit') || message.includes('throttl')) {
    return '请求频率过高，请稍后重试或联系管理员调整频率';
  }

  if (message.includes('auth') || message.includes('token') || message.includes('credential')) {
    return '认证失败，请检查Amazon凭据配置或刷新访问令牌';
  }

  if (message.includes('marketplace') || message.includes('permission')) {
    return '市场访问权限不足，请检查凭据的市场配置';
  }

  if (message.includes('order not found') || message.includes('not exist')) {
    return '订单在Amazon平台不存在或已被删除';
  }

  return '请联系技术支持或查看详细错误日志';
}

// ============= 同步历史管理 =============

/**
 * 获取同步历史记录
 */
export async function getSyncHistory(params?: {
  current?: number;
  pageSize?: number;
  status?: string;
  sync_type?: string;
  start_date?: string;
  end_date?: string;
}) {
  return get<Array<{
    id: number;
    sync_time: string;
    sync_type: 'manual' | 'scheduled' | 'auto';
    processed_count: number;
    success_count: number;
    failed_count: number;
    status: 'completed' | 'failed' | 'partial';
    duration: number;
    trigger_user?: string;
    error_message?: string;
    details?: Array<{
      id: number;
      step: string;
      status: 'pending' | 'processing' | 'completed' | 'failed';
      start_time: string;
      end_time?: string;
      duration?: number;
      message?: string;
      processed_count?: number;
      error_count?: number;
    }>;
    created_at: string;
    updated_at: string;
  }>>('/api/tenant/amazon/sync/history', params);
}
